import {
  clearDashboardSession,
  getAuthSession,
  getCompanyAuthPayload,
  setAuthSession,
} from "./sessionUtils.js";

const inflightJsonRequests = new Map();

const buildRequestDeduplicationKey = ({ url, data, isFormData }) => {
  if (isFormData) {
    return null;
  }

  return JSON.stringify({
    url,
    data,
  });
};

const requestWithAuth = async ({
  url,
  data = {},
  navigate,
  isFormData = false,
}) => {
  const dedupeKey = buildRequestDeduplicationKey({ url, data, isFormData });

  if (dedupeKey && inflightJsonRequests.has(dedupeKey)) {
    return inflightJsonRequests.get(dedupeKey);
  }

  const requestPromise = (async () => {
  const buildHeaders = (token) => {
    const headers = {};

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    if (!isFormData) {
      headers["Content-Type"] = "application/json";
    }

    return headers;
  };

  const redirectToLogin = () => {
    clearDashboardSession();

    if (typeof navigate === "function") {
      navigate("/login");
      return;
    }

    window.location.href = "/login";
  };

  const { accessToken } = getAuthSession();
  let headers = buildHeaders(accessToken);

  let res = await fetch(`${process.env.REACT_APP_BASE_URL}${url}`, {
    method: "POST",
    headers: headers,
    body: isFormData ? data : JSON.stringify(data),
  });

  let response = await res.json();

  if (response.code === 401 || response.code === 403) {
    // 401 - 토큰 재발급 api 호출
    const newTokens = await callRefreshToken(navigate);

    if (newTokens) {
      const nextAccessToken = newTokens.data.accessToken;

      setAuthSession({
        accessToken: nextAccessToken,
      });

      headers = buildHeaders(nextAccessToken);

      res = await fetch(`${process.env.REACT_APP_BASE_URL}${url}`, {
        method: "POST",
        headers: headers,
        body: isFormData ? data : JSON.stringify(data),
      });

      response = await res.json();
    } else {
      redirectToLogin();
    }
  }
  return response;
  })();

  if (!dedupeKey) {
    return requestPromise;
  }

  inflightJsonRequests.set(dedupeKey, requestPromise);

  try {
    return await requestPromise;
  } finally {
    inflightJsonRequests.delete(dedupeKey);
  }
};

export const fetchWithAuth = async (
  url,
  data = {},
  navigate,
  isFormData = false
) => requestWithAuth({ url, data, navigate, isFormData });

export const postJson = async (url, data = {}, navigate) =>
  requestWithAuth({ url, data, navigate, isFormData: false });

export const postFormData = async (url, formData, navigate) =>
  requestWithAuth({ url, data: formData, navigate, isFormData: true });

export const withCompanyAuthPayload = (data = {}) => ({
  ...getCompanyAuthPayload(),
  ...data,
});

export const appendCompanyAuthToFormData = (formData) => {
  const nextFormData = formData;
  const { companyId, companyCode } = getCompanyAuthPayload();

  if (companyId && !nextFormData.has("companyId")) {
    nextFormData.append("companyId", companyId);
  }

  if (companyCode && !nextFormData.has("companyCode")) {
    nextFormData.append("companyCode", companyCode);
  }

  return nextFormData;
};

export const postCompanyJson = async (url, data = {}, navigate) =>
  postJson(url, withCompanyAuthPayload(data), navigate);

export const postCompanyFormData = async (url, formData, navigate) =>
  postFormData(url, appendCompanyAuthToFormData(formData), navigate);

const callRefreshToken = async (navigate) => {
  try {
    const res = await fetch(
      `${process.env.REACT_APP_BASE_URL}/dashboard/auth/token`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({}),
      }
    );

    const response = await res.json();

    if (response.code === 200) {
      return response;
    } else if (response.code === 401) {
      console.log("리프레시 토큰 만료");
      if (typeof navigate === "function") {
        navigate("/login");
      } else {
        window.location.href = "/login";
      }

      return null;
    } else if (response.code === 403) {
      console.log("유효하지 않은 토큰. 재로그인 필요");
      if (typeof navigate === "function") {
        navigate("/login");
      } else {
        window.location.href = "/login";
      }
      return null;
    }
  } catch (error) {
    console.error("토큰 재발급 중 에러:", error);
    return null;
  }
};
